import { useProjectNavigation } from "@/lib/hooks/modal/useProjectNavigation";
import { Button, LoadingSpinner, Modal, SearchBar } from "@ai-teach/ui-web";
import { useState } from "react";
import { HiFolder } from "react-icons/hi";
import { MdChevronRight } from "react-icons/md";

export interface PickedProject {
  id: string;
  name: string;
}

interface ProjectPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (project: PickedProject) => void;
}

/** The server rejects a limit above 100, so one page of 100 is the most the picker can load. */
const PICKER_LIMIT = 100;

/** Browse the project tree and pick one. Rows open a folder; Select chooses it. */
export function ProjectPickerModal({ isOpen, onClose, onSelect }: ProjectPickerModalProps) {
  const [searchText, setSearchText] = useState("");

  const {
    projects,
    projectsLoading,
    currentParent,
    navigationStack,
    handleProjectClick,
    navigateToIndex,
    resetNavigation,
  } = useProjectNavigation(searchText, undefined, PICKER_LIMIT);

  const handleClose = () => {
    resetNavigation();
    setSearchText("");
    onClose();
  };

  const pick = (project: PickedProject) => {
    onSelect(project);
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Choose a project" size="lg">
      <div className="space-y-4">
        <SearchBar value={searchText} onChange={setSearchText} placeholder="Search projects..." />

        <nav
          aria-label="Folder breadcrumb"
          className="flex min-w-0 flex-wrap items-center gap-1 overflow-hidden text-sm"
        >
          <button
            type="button"
            onClick={() => navigateToIndex(-1)}
            className={`flex-shrink-0 font-medium transition-colors ${
              navigationStack.length === 0
                ? "cursor-default text-secondary-200"
                : "text-muted hover:text-primary-500"
            }`}
          >
            Home
          </button>
          {navigationStack.map((item, i) => (
            <span key={item.id} className="flex min-w-0 items-center gap-1">
              <MdChevronRight className="h-4 w-4 flex-shrink-0 text-muted" />
              <button
                type="button"
                onClick={() => navigateToIndex(i)}
                className={`truncate font-medium transition-colors ${
                  i === navigationStack.length - 1
                    ? "cursor-default text-secondary-200"
                    : "text-muted hover:text-primary-500"
                }`}
              >
                {item.name}
              </button>
            </span>
          ))}
        </nav>

        {currentParent && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-primary-500/30 bg-primary-500/10 px-3 py-2">
            <p className="truncate text-sm text-secondary-200">
              Inside <span className="font-semibold text-secondary-200">{currentParent.name}</span>
            </p>
            <Button variant="secondary" onClick={() => pick(currentParent)}>
              Select this one
            </Button>
          </div>
        )}

        <div className="gradient-scrollbar h-[60vh] overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800">
          {projectsLoading && projects.length === 0 ? (
            <div className="caption flex h-full flex-col items-center justify-center gap-2 p-4">
              <LoadingSpinner />
              <p className="animate-pulse">Loading...</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 py-8 text-center">
              <HiFolder className="h-8 w-8 text-primary-500" />
              <p className="text-muted text-sm">
                {currentParent ? "No projects in this folder" : "No projects found"}
              </p>
            </div>
          ) : (
            projects.map((project) => (
              <div
                key={project.id}
                className="flex items-center justify-between gap-2 border-gray-200 border-b p-3 last:border-b-0 hover:bg-gray-100 dark:border-gray-800 dark:hover:bg-gray-800"
              >
                <button
                  type="button"
                  onClick={() => handleProjectClick({ id: project.id, name: project.name })}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                  <HiFolder className="h-5 w-5 flex-shrink-0 text-secondary-200" />
                  <span className="truncate font-medium text-secondary-200">{project.name}</span>
                </button>
                <Button
                  variant="secondary"
                  onClick={() => pick({ id: project.id, name: project.name })}
                >
                  Select
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
